import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../Buttons/IconButton';
import ArrowLeft from '../../Icons/16x16/ArrowLeft';
import Close16 from '../../Icons/16x16/Close';
import PopoverHeadingText from '../PopoverHeadingText';
import SpreadLayout from '../SpreadLayout';
import s from './PopoverHeader.scss';

export interface Props {
  /** The popover's heading */
  heading: string;

  /** The popover's optional subheading */
  subheading?: string;

  /** A slot for an illustration of some sort. Rendered before the heading text */
  illustration?: React.ReactNode;

  className?: string;

  /** Will cause a back button to be rendered at the start of the header */
  onBackClick?: () => void;

  /** Will cause a close button to be rendered at the end of the header */
  onCloseClick?: () => void;
}

/** A wrapper around left-side items to help keep the main component body clean */
const LeftItem: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
  <div className={s['left-item-wrapper']}>{children}</div>
);

/** Our standard Popover header. */
const PopoverHeader: React.FC<React.PropsWithChildren<Props>> = ({
  heading,
  subheading,
  illustration,
  onBackClick,
  onCloseClick,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <SpreadLayout
      className={className}
      left={[
        onBackClick && (
          <LeftItem key="back-button">
            <IconButton
              key="back-button"
              icon={ArrowLeft}
              size="small"
              onClick={onBackClick}
              aria-label={t('common.back')}
            />
          </LeftItem>
        ),
        illustration && <LeftItem key="illustration">{illustration}</LeftItem>,
        <LeftItem key="heading">
          <PopoverHeadingText>{heading}</PopoverHeadingText>
          {subheading && <PopoverHeadingText isSubheading={true}>{subheading}</PopoverHeadingText>}
        </LeftItem>,
      ]}
      right={
        onCloseClick && (
          <IconButton
            icon={Close16}
            aria-label={t('common.close')}
            size="medium"
            onClick={onCloseClick}
          />
        )
      }
    />
  );
};
PopoverHeader.displayName = 'PopoverHeader';

export default PopoverHeader;
